/**
 * Shopify Mapping Review Queue
 *
 * Lists active Shopify variants (all four Dosaze stores) that the auto-matcher
 * could not link to a SKU-master item. For each one, pick the right item and
 * Assign, or Ignore it (e.g. Route insurance, retired listings). Decisions
 * persist across syncs.
 */

import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { SkuItem, UnmatchedVariant } from "services/SkuMasterService";

interface MappingReviewDialogProps {
  open: boolean;
  onClose: () => void;
  unmatched: UnmatchedVariant[] | null;
  items: SkuItem[];
  onAssign: (variant: UnmatchedVariant, itemId: number) => Promise<boolean>;
  onIgnore: (variant: UnmatchedVariant) => Promise<boolean>;
}

const STORE_COLORS: Record<string, "default" | "primary" | "secondary" | "success"> = {
  US: "primary", AU: "success", CA: "secondary", UK: "default",
};

export default function MappingReviewDialog({
  open,
  onClose,
  unmatched,
  items,
  onAssign,
  onIgnore,
}: MappingReviewDialogProps): JSX.Element {
  const [selectedItemByVariant, setSelectedItemByVariant] = useState<Record<string, SkuItem | null>>({});
  const [busyVariant, setBusyVariant] = useState<string | null>(null);

  const variantKey = (variant: UnmatchedVariant) => `${variant.store}:${variant.variantId}`;

  const handleAssign = async (variant: UnmatchedVariant) => {
    const item = selectedItemByVariant[variantKey(variant)];
    if (!item) return;
    setBusyVariant(variantKey(variant));
    try {
      await onAssign(variant, item.shipbobInventoryId);
    } finally {
      setBusyVariant(null);
    }
  };

  const handleIgnore = async (variant: UnmatchedVariant) => {
    setBusyVariant(variantKey(variant));
    try {
      await onIgnore(variant);
    } finally {
      setBusyVariant(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Shopify mapping review{unmatched ? ` (${unmatched.length} unmatched)` : ""}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These active Shopify variants could not be auto-matched to a SKU-master
          item. Assign each to the right item, or Ignore listings that don't
          correspond to a physical product.
        </Typography>
        {unmatched === null ? (
          <Typography variant="body2">Loading…</Typography>
        ) : unmatched.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Queue is empty — every active variant is mapped, ignored, or bundle-linked. 🎉
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={70}>Store</TableCell>
                <TableCell>Shopify listing</TableCell>
                <TableCell width={170}>Variant SKU</TableCell>
                <TableCell width={340}>Map to item</TableCell>
                <TableCell width={170} />
              </TableRow>
            </TableHead>
            <TableBody>
              {unmatched.map(variant => {
                const key = variantKey(variant);
                return (
                  <TableRow key={key}>
                    <TableCell>
                      <Chip label={variant.store} size="small" color={STORE_COLORS[variant.store]} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {variant.title}
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        variant {variant.variantId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {variant.sku || "—"}
                      {variant.ambiguousSku && (
                        <Tooltip title="This SKU exists on more than one ShipBob item — pick the right one">
                          <Chip label="ambiguous" size="small" color="warning" sx={{ ml: 0.5 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        size="small"
                        options={items}
                        value={selectedItemByVariant[key] || null}
                        onChange={(_, value) => setSelectedItemByVariant(prev => ({ ...prev, [key]: value }))}
                        getOptionLabel={(item) => `${item.shipbobSku || item.shipbobInventoryId} — ${item.name || ""}`}
                        isOptionEqualToValue={(a, b) => a.shipbobInventoryId === b.shipbobInventoryId}
                        renderInput={(params) => <TextField {...params} placeholder="Search items…" />}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!selectedItemByVariant[key] || busyVariant === key}
                          onClick={() => handleAssign(variant)}
                        >
                          Assign
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          color="inherit"
                          disabled={busyVariant === key}
                          onClick={() => handleIgnore(variant)}
                        >
                          Ignore
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
